/**
 * Boxing Ring Portfolio
 * - Filterable gallery (data-category)
 * - Lightbox with prev/next, ESC, focus return
 * - "Inquire" buttons prefill the form's ring-of-interest input + smooth scroll
 *
 * Each section is independent; multiple instances per page are supported.
 */

class BoxingRingPortfolio {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('[data-brp-grid]');
    this.cards = Array.from(root.querySelectorAll('.brp__card'));
    this.filterButtons = Array.from(root.querySelectorAll('[data-brp-filter]'));
    this.emptyMsg = root.querySelector('[data-brp-empty]');
    this.lightbox = root.querySelector('[data-brp-lightbox]');
    this.productInput = root.querySelector('[data-brp-product-input]');

    this.lastFocused = null;
    this.activeIndex = -1;
    this.visibleTriggers = [];

    this.#teleportLightbox();
    this.#cacheLightboxNodes();
    this.#bindFilters();
    this.#bindGalleryTriggers();
    this.#bindLightboxControls();
    this.#bindScrollAndPrefill();
    this.#bindFeaturedTrack();
    this.#bindHeroSlideshow();
    this.#hideShopifyChat();
  }

  #hideShopifyChat() {
    document.body.classList.add('brp-page');

    const hide = () => {
      const chatElements = document.querySelectorAll('#shopify-chat, #ShopifyChat, inbox-online-store-chat');

      for (const el of chatElements) {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      }
    };

    hide();

    const observer = new MutationObserver(hide);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  #bindHeroSlideshow() {
    const hero = this.root.querySelector('[data-brp-hero]');

    if (!hero) {
      return;
    }

    const slides = Array.from(hero.querySelectorAll('[data-brp-slide]'));

    if (slides.length === 0) {
      return;
    }

    let activeIndex = 0;
    slides[activeIndex].classList.add('is-active');

    if (slides.length < 2) {
      return;
    }

    const interval = Number.parseInt(hero.getAttribute('data-interval'), 10) || 5000;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      return;
    }

    let timer = null;

    const advance = () => {
      slides[activeIndex].classList.remove('is-active');
      activeIndex = (activeIndex + 1) % slides.length;
      slides[activeIndex].classList.add('is-active');
    };

    const start = () => {
      stop();
      timer = window.setInterval(advance, interval);
    };

    const stop = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    start();

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    });
  }

  #teleportLightbox() {
    if (!this.lightbox || this.lightbox.parentElement === document.body) {
      return;
    }

    document.body.appendChild(this.lightbox);
  }

  #bindFeaturedTrack() {
    const track = this.root.querySelector('[data-brp-track]');
    const prev = this.root.querySelector('[data-brp-track-prev]');
    const next = this.root.querySelector('[data-brp-track-next]');

    if (!track || !prev || !next) {
      return;
    }

    const getStep = () => {
      const card = track.querySelector('.brp__featured-card');

      if (!card) {
        return track.clientWidth;
      }

      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const updateState = () => {
      const maxScroll = track.scrollWidth - track.clientWidth - 1;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= maxScroll;
    };

    prev.addEventListener('click', () => {
      track.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      track.scrollBy({ left: getStep(), behavior: 'smooth' });
    });

    track.addEventListener('scroll', updateState, { passive: true });
    window.addEventListener('resize', updateState);
    requestAnimationFrame(updateState);
  }

  #cacheLightboxNodes() {
    if (!this.lightbox) {
      return;
    }

    this.lb = {
      image: this.lightbox.querySelector('[data-brp-image-target]'),
      video: this.lightbox.querySelector('[data-brp-video-target]'),
      nativeVideo: this.lightbox.querySelector('[data-brp-native-video-target]'),
      title: this.lightbox.querySelector('[data-brp-title-target]'),
      tag: this.lightbox.querySelector('[data-brp-tag-target]'),
      desc: this.lightbox.querySelector('[data-brp-desc-target]'),
      locationWrap: this.lightbox.querySelector('[data-brp-location-wrap]'),
      locationText: this.lightbox.querySelector('[data-brp-location-target]'),
      sizeWrap: this.lightbox.querySelector('[data-brp-size-wrap]'),
      sizeText: this.lightbox.querySelector('[data-brp-size-target]'),
      prev: this.lightbox.querySelector('[data-brp-prev]'),
      next: this.lightbox.querySelector('[data-brp-next]'),
    };
  }

  #bindFilters() {
    for (const btn of this.filterButtons) {
      btn.addEventListener('click', () => this.#applyFilter(btn));
      btn.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.#applyFilter(btn);
        }
      });
    }
  }

  #applyFilter(button) {
    const filter = button.getAttribute('data-brp-filter');

    for (const b of this.filterButtons) {
      const isActive = b === button;
      b.classList.toggle('brp__filter--active', isActive);
      b.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }

    let visibleCount = 0;

    for (const card of this.cards) {
      const cat = card.getAttribute('data-category');
      const isUniversal = cat === '*';
      const show = isUniversal || filter === 'all' || cat === filter;
      card.dataset.hidden = show ? 'false' : 'true';

      if (show) {
        visibleCount += 1;
      }
    }

    if (this.emptyMsg) {
      this.emptyMsg.hidden = visibleCount > 0;
    }
  }

  #bindGalleryTriggers() {
    const triggers = this.root.querySelectorAll('[data-brp-open]');

    for (const trigger of triggers) {
      trigger.addEventListener('click', () => {
        this.#openLightboxAt(trigger);
      });
    }
  }

  #getVisibleTriggers() {
    return this.cards
      .filter((card) => card.dataset.hidden !== 'true')
      .map((card) => card.querySelector('[data-brp-open]'))
      .filter(Boolean);
  }

  #openLightboxAt(trigger) {
    if (!this.lightbox || !this.lb) {
      return;
    }

    this.visibleTriggers = this.#getVisibleTriggers();
    this.activeIndex = this.visibleTriggers.indexOf(trigger);

    if (this.activeIndex === -1) {
      this.visibleTriggers = [trigger];
      this.activeIndex = 0;
    }

    this.lastFocused = document.activeElement;
    this.#renderLightbox(trigger);
    this.lightbox.hidden = false;
    document.documentElement.classList.add('brp-lightbox-open');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      const closeBtn = this.lightbox.querySelector('.brp__lightbox-close');

      if (closeBtn) {
        closeBtn.focus();
      }
    });
  }

  #renderLightbox(trigger) {
    const data = {
      image: trigger.getAttribute('data-brp-image') || '',
      videoId: trigger.getAttribute('data-brp-video-id') || '',
      videoType: trigger.getAttribute('data-brp-video-type') || '',
      nativeVideoSrc: trigger.getAttribute('data-brp-native-video-src') || '',
      nativeVideoType: trigger.getAttribute('data-brp-native-video-type') || '',
      videoStart: trigger.getAttribute('data-brp-video-start') || '0',
      title: trigger.getAttribute('data-brp-title') || '',
      location: trigger.getAttribute('data-brp-location') || '',
      size: trigger.getAttribute('data-brp-size') || '',
      category: trigger.getAttribute('data-brp-category') || '',
      description: trigger.getAttribute('data-brp-description') || '',
    };

    this.#renderLightboxMedia(data);

    if (this.lb.title) {
      this.lb.title.textContent = data.title;
    }

    if (this.lb.tag) {
      this.lb.tag.textContent = data.category;
    }

    if (this.lb.desc) {
      this.lb.desc.textContent = data.description;
    }

    if (this.lb.locationWrap && this.lb.locationText) {
      this.lb.locationText.textContent = data.location;
      this.lb.locationWrap.hidden = !data.location;
    }

    if (this.lb.sizeWrap && this.lb.sizeText) {
      this.lb.sizeText.textContent = data.size;
      this.lb.sizeWrap.hidden = !data.size;
    }

    const showNav = this.visibleTriggers.length > 1;

    if (this.lb.prev) {
      this.lb.prev.hidden = !showNav;
    }

    if (this.lb.next) {
      this.lb.next.hidden = !showNav;
    }
  }

  #renderLightboxMedia(data) {
    const hasVideo = data.videoId && data.videoType;
    const hasNativeVideo = data.nativeVideoSrc;

    if (this.lb.video) {
      this.lb.video.hidden = !hasVideo;
      this.lb.video.src = hasVideo ? this.#getEmbedUrl(data.videoType, data.videoId, data.videoStart) : '';
      this.lb.video.title = data.title || 'Project video';
    }

    if (this.lb.nativeVideo) {
      this.lb.nativeVideo.hidden = !hasNativeVideo;
      this.lb.nativeVideo.innerHTML = '';

      if (hasNativeVideo) {
        this.lb.nativeVideo.muted = true;
        this.lb.nativeVideo.autoplay = true;
        this.lb.nativeVideo.loop = true;
        this.lb.nativeVideo.controls = false;
        this.lb.nativeVideo.setAttribute('muted', '');
        this.lb.nativeVideo.setAttribute('autoplay', '');
        this.lb.nativeVideo.setAttribute('loop', '');
        this.lb.nativeVideo.setAttribute('playsinline', '');

        const source = document.createElement('source');
        source.src = data.nativeVideoSrc;

        if (data.nativeVideoType) {
          source.type = data.nativeVideoType;
        }

        this.lb.nativeVideo.appendChild(source);
        this.lb.nativeVideo.load();
        this.lb.nativeVideo.play().catch(() => {});
      }
    }

    if (this.lb.image) {
      this.lb.image.hidden = hasVideo || hasNativeVideo;
      this.lb.image.src = hasVideo || hasNativeVideo ? '' : data.image;
      this.lb.image.alt = data.title || '';
    }
  }

  #getEmbedUrl(type, id, start) {
    const encodedId = encodeURIComponent(id);
    const startSeconds = Number.parseInt(start, 10);
    const startParam = Number.isNaN(startSeconds) || startSeconds <= 0 ? '' : `&start=${startSeconds}`;

    if (type === 'youtube') {
      return `https://www.youtube.com/embed/${encodedId}?autoplay=1&mute=1&controls=0&rel=0&modestbranding=1&playsinline=1&loop=1&playlist=${encodedId}${startParam}`;
    }

    if (type === 'vimeo') {
      return `https://player.vimeo.com/video/${encodedId}?autoplay=1&muted=1&loop=1&controls=0&title=0&byline=0&portrait=0`;
    }

    return '';
  }

  #closeLightbox() {
    if (!this.lightbox) {
      return;
    }

    this.lightbox.hidden = true;
    if (this.lb?.video) {
      this.lb.video.src = '';
    }
    if (this.lb?.nativeVideo) {
      this.lb.nativeVideo.pause();
      this.lb.nativeVideo.removeAttribute('src');
      this.lb.nativeVideo.innerHTML = '';
      this.lb.nativeVideo.load();
    }
    document.documentElement.classList.remove('brp-lightbox-open');
    document.body.style.overflow = '';

    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
  }

  #step(direction) {
    if (this.visibleTriggers.length === 0) {
      return;
    }

    const len = this.visibleTriggers.length;
    this.activeIndex = (this.activeIndex + direction + len) % len;
    const next = this.visibleTriggers[this.activeIndex];

    if (next) {
      this.#renderLightbox(next);
    }
  }

  #bindLightboxControls() {
    if (!this.lightbox) {
      return;
    }

    const handleClose = (event) => {
      const target = event.target.closest('[data-brp-close]');

      if (!target) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.#closeLightbox();
    };

    this.lightbox.addEventListener('click', handleClose);
    this.lightbox.addEventListener('touchend', handleClose, { passive: false });

    if (this.lb.prev) {
      this.lb.prev.addEventListener('click', () => this.#step(-1));
    }

    if (this.lb.next) {
      this.lb.next.addEventListener('click', () => this.#step(1));
    }

    document.addEventListener('keydown', (event) => {
      if (this.lightbox.hidden) {
        return;
      }

      if (event.key === 'Escape') {
        this.#closeLightbox();
      } else if (event.key === 'ArrowRight') {
        this.#step(1);
      } else if (event.key === 'ArrowLeft') {
        this.#step(-1);
      }
    });
  }

  #bindScrollAndPrefill() {
    const scrollers = this.root.querySelectorAll('[data-brp-scroll]');

    for (const link of scrollers) {
      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href') || '';

        if (!href.startsWith('#')) {
          return;
        }

        const target = this.root.querySelector(href) || document.querySelector(href);

        if (!target) {
          return;
        }

        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });

        const prefillValue = link.getAttribute('data-brp-prefill');

        if (prefillValue && this.productInput) {
          this.productInput.value = prefillValue;
          this.productInput.dispatchEvent(new Event('input', { bubbles: true }));
        }

        const focusable = target.querySelector('input, select, textarea, button');

        if (focusable) {
          window.setTimeout(() => focusable.focus({ preventScroll: true }), 450);
        }
      });
    }
  }
}

const initBoxingRingPortfolio = () => {
  const sections = document.querySelectorAll('[data-brp]');

  for (const section of sections) {
    if (section.dataset.brpInited === 'true') {
      continue;
    }

    section.dataset.brpInited = 'true';
    new BoxingRingPortfolio(section);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBoxingRingPortfolio);
} else {
  initBoxingRingPortfolio();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.querySelector('[data-brp]');

  if (root && root.dataset.brpInited !== 'true') {
    root.dataset.brpInited = 'true';
    new BoxingRingPortfolio(root);
  }
});
